import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import QrCodeOutlinedIcon from "@mui/icons-material/QrCodeOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useSnackbar } from "notistack";
import { isLeadCaptureTracker, isTrackerPublicLinkAvailable, SUBMISSION_MODES, getPublicJoinBaseUrl } from "./trackerStorage";
import { trackerFieldSx as fieldSx } from "./trackerFormStyles";
import { uploadImageOns3 } from "src/DAL/commonApi/commonApi";
import { s3baseUrl } from "src/config/config";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const SURFACE = "#f8fafc";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: PRIMARY },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: PRIMARY,
  },
};

const HelperText = ({ children, sx }) => (
  <Typography
    sx={{ fontSize: 12, color: MUTED, lineHeight: 1.45, mt: 0.75, ...sx }}
  >
    {children}
  </Typography>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ mb: 0.85, fontWeight: 600, fontSize: 13, color: "#334155" }}>
    {children}
  </Typography>
);

const IconBadge = ({ children }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: "10px",
      bgcolor: PRIMARY_SOFT,
      color: PRIMARY,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const Section = ({ icon, title, description, action, children, last }) => (
  <Box
    component="section"
    sx={{
      py: 3,
      px: 0,
      borderBottom: last ? "none" : `1px solid ${BORDER}`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        mb: 2.25,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
        <IconBadge>{icon}</IconBadge>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 15.5, color: "#0f172a" }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              sx={{
                mt: 0.4,
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Box>
    {children}
  </Box>
);

const SettingPanel = ({ children, sx }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: SURFACE,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SettingRow = ({ title, description, control, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#0f172a" }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.35, lineHeight: 1.45 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
    {control ? <Box sx={{ flexShrink: 0, pt: 0.25 }}>{control}</Box> : null}
  </Box>
);

const buildDraftFromTracker = (tracker) => ({
  coaches: tracker.coaches || [],
  submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
  publicSlug: tracker.publicSlug || tracker.id || "",
  backgroundImage: tracker.backgroundImage || "",
  settings: {
    requirePassword: false,
    password: "",
    submissionLimit: "",
    confirmationMessage: "",
    confirmationEmailTemplate: "",
    confirmationEmailEnabled: false,
    noMatchBehaviour: "create",
    acceptingSubmissions: true,
    excelExportEnabled: true,
    recordSubmittedBy: true,
    ...(tracker.settings || {}),
  },
  gymTv: {
    enabled: false,
    headline: tracker.name || "",
    showTop: 10,
    eligibleStatusIds: [],
    ...(tracker.gymTv || {}),
  },
  gymTvViews: tracker.gymTvViews || [],
  status: tracker.status || "draft",
});

const sanitizePublicSlug = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-");

const PublicLinkEditor = ({
  baseUrl,
  slug,
  onSlugChange,
  onCopy,
  onOpenQr,
  fallbackSlug = "tracker",
  size = "small",
  label = "Public link",
}) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={slug ?? ""}
    onChange={(e) => onSlugChange(sanitizePublicSlug(e.target.value))}
    onBlur={() => {
      const cleaned = sanitizePublicSlug(slug).replace(/^-+|-+$/g, "");
      // Empty on blur → restore a usable default so the link stays valid.
      onSlugChange(cleaned || sanitizePublicSlug(fallbackSlug).replace(/^-+|-+$/g, "") || "tracker");
    }}
    placeholder="your-slug"
    inputProps={{
      "aria-label": label,
      spellCheck: false,
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment
          position="start"
          sx={{
            mr: 0.75,
            maxWidth: { xs: "42%", sm: "none" },
            "& .MuiTypography-root": {
              fontSize: 12.5,
              color: MUTED,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            },
          }}
        >
          <Typography title={baseUrl}>{baseUrl}</Typography>
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end" sx={{ ml: 0.25 }}>
          <IconButton onClick={onCopy} title="Copy link" size={size} edge="end">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onOpenQr} title="QR code" size={size} edge="end">
            <QrCodeOutlinedIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ),
    }}
    sx={{
      ...fieldSx,
      "& input": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 13,
      },
    }}
  />
);

export default function PublishSettingsDrawer({
  open = false,
  variant = "drawer",
  hideHeader = false,
  tracker,
  publicUrl,
  onClose,
  onChange,
  onEditCoaches,
  onOpenQr,
  onPublish,
  publishing = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const isInline = variant === "inline";
  const hideGymTv = isLeadCaptureTracker(tracker);

  useEffect(() => {
    if (!tracker) return;
    if (!isInline && !open) return;
    const next = buildDraftFromTracker(tracker);
    if (isLeadCaptureTracker(tracker)) {
      next.gymTv = { ...(next.gymTv || {}), enabled: false };
    }
    setDraft(next);
  }, [open, tracker, isInline]);

  const push = (next) => {
    const gymTv = hideGymTv
      ? { ...(next.gymTv || {}), enabled: false }
      : next.gymTv;
    const shaped = hideGymTv ? { ...next, gymTv } : next;
    setDraft(shaped);
    onChange?.({
      coaches: shaped.coaches,
      submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
      publicSlug: shaped.publicSlug,
      backgroundImage: shaped.backgroundImage || "",
      settings: shaped.settings,
      gymTv: shaped.gymTv,
      gymTvViews: shaped.gymTvViews,
      status: shaped.status,
    });
  };

  const updateSettings = (patch) => {
    push({
      ...draft,
      settings: { ...draft.settings, ...patch },
    });
  };

  const updateGymTv = (patch) => {
    if (hideGymTv) return;
    push({
      ...draft,
      gymTv: { ...draft.gymTv, ...patch },
    });
  };

  const resolveBgPreview = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${s3baseUrl || ""}${path}`;
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !draft) return;
    if (!String(file.type || "").startsWith("image/")) {
      enqueueSnackbar("Please upload an image file (JPG, PNG, WEBP)", {
        variant: "warning",
      });
      return;
    }
    setUploadingBg(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("width", "1600");
      const imageUpload = await uploadImageOns3(formData);
      if (imageUpload?.code === 200 && imageUpload.image_path) {
        push({
          ...draft,
          backgroundImage: imageUpload.image_path,
        });
        enqueueSnackbar("Background image uploaded", { variant: "success" });
      } else {
        enqueueSnackbar(
          imageUpload?.message || "Image upload failed",
          { variant: "error" }
        );
      }
    } catch (_err) {
      enqueueSnackbar("Image upload failed", { variant: "error" });
    } finally {
      setUploadingBg(false);
    }
  };

  const clearBackgroundImage = () => {
    if (!draft) return;
    push({ ...draft, backgroundImage: "" });
  };

  const publicBaseUrl = getPublicJoinBaseUrl();
  const publicSlugValue = draft ? draft.publicSlug ?? "" : "";
  const slugFallback =
    sanitizePublicSlug(tracker?.name || "")
      .replace(/^-+|-+$/g, "") ||
    sanitizePublicSlug(tracker?.id || "tracker").replace(/^-+|-+$/g, "") ||
    "tracker";
  const composedPublicUrl = `${publicBaseUrl}${publicSlugValue || slugFallback}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(composedPublicUrl || publicUrl);
      enqueueSnackbar("Public link copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy link", { variant: "error" });
    }
  };

  const setPublicSlug = (publicSlug) => {
    push({
      ...draft,
      publicSlug,
    });
  };

  const statuses = tracker?.statuses || [];
  const isArchived = draft?.status === "archived";
  const isLive =
    !isArchived &&
    (draft?.status === "live" || draft?.status === "published");
  const publicLinkAvailable = isTrackerPublicLinkAvailable(tracker);

  const eligibleIds = useMemo(
    () => new Set(draft?.gymTv?.eligibleStatusIds || []),
    [draft]
  );

  if (!draft) return null;

  const submissionLimitUnlimited =
    draft.settings.submissionLimit === "" ||
    draft.settings.submissionLimit == null;

  // Upload / Replace sits inside the image card (no separate button row).
  const uploadImageButton = (
    <Box
      component="label"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.85,
        borderRadius: "8px",
        border: `1px solid ${BORDER}`,
        bgcolor: "#fff",
        cursor: uploadingBg ? "wait" : "pointer",
        fontSize: 13,
        fontWeight: 650,
        color: "#0f172a",
        opacity: uploadingBg ? 0.7 : 1,
        "&:hover": { borderColor: PRIMARY, color: PRIMARY },
      }}
    >
      {uploadingBg ? (
        <CircularProgress size={16} thickness={5} />
      ) : (
        <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
      )}
      {uploadingBg
        ? "Uploading…"
        : draft.backgroundImage
          ? "Replace image"
          : "Upload image"}
      <input
        hidden
        type="file"
        accept="image/*"
        disabled={uploadingBg}
        onChange={handleBackgroundUpload}
      />
    </Box>
  );

  const renderBackgroundPicker = (height) =>
    draft.backgroundImage ? (
      <Box
        sx={{
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          bgcolor: SURFACE,
          height,
        }}
      >
        <Box
          component="img"
          src={resolveBgPreview(draft.backgroundImage)}
          alt="Public page background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        <IconButton
          size="small"
          onClick={clearBackgroundImage}
          aria-label="Remove background image"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(15,23,42,0.72)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(15,23,42,0.9)" },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.18)",
            borderRadius: "8px",
          }}
        >
          {uploadImageButton}
        </Box>
      </Box>
    ) : (
      <Box
        sx={{
          minHeight: height,
          borderRadius: "12px",
          border: `1px dashed ${BORDER}`,
          bgcolor: SURFACE,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 2,
          py: 2.5,
          color: MUTED,
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 28, opacity: 0.7 }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
          No background image yet
        </Typography>
        {uploadImageButton}
      </Box>
    );

  const header = (
    <Box
      sx={{
        px: isInline ? 0 : 3,
        py: isInline ? 0 : 2.25,
        mb: isInline ? 1 : 0,
        borderBottom: isInline ? "none" : `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ pr: isInline ? 0 : 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          {isInline ? (
            <LinkOutlinedIcon fontSize="small" sx={{ color: PRIMARY }} />
          ) : null}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isInline ? 16 : 20,
              color: "#0f172a",
            }}
          >
            Publish settings
          </Typography>
        </Box>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: 13,
            color: MUTED,
            lineHeight: 1.5,
            ml: isInline ? 3.25 : 0,
          }}
        >
          {hideGymTv
            ? "Public link and duplicate handling"
            : "Public link, duplicate handling and GymTV"}
        </Typography>
      </Box>
      {!isInline ? (
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </Box>
  );

  const inlineBody = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {!publicLinkAvailable ? (
          <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.45 }}>
            Public link, password and submission limit are not available for
            GymRite members — set Journey Access to Public on Basics for a
            shared URL.
          </Typography>
        ) : (
          <>
            <PublicLinkEditor
              baseUrl={publicBaseUrl}
              slug={publicSlugValue}
              onSlugChange={setPublicSlug}
              onCopy={copyLink}
              onOpenQr={onOpenQr}
              fallbackSlug={slugFallback}
              size="small"
              label="Public link"
            />

            {draft.settings.requirePassword ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Password protection"
                    value="protected"
                    onChange={(e) => {
                      const requirePassword = e.target.value === "protected";
                      updateSettings({
                        requirePassword,
                        ...(requirePassword ? {} : { password: "" }),
                      });
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value="open">No password</MenuItem>
                    <MenuItem value="protected">Password protected</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Public access password"
                    placeholder="Set access password..."
                    value={draft.settings.password || ""}
                    onChange={(e) =>
                      updateSettings({ password: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon
                            sx={{ fontSize: 18, color: MUTED }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Submissions limit"
                    value={
                      submissionLimitUnlimited ? "unlimited" : "limited"
                    }
                    onChange={(e) => {
                      if (e.target.value === "unlimited") {
                        updateSettings({ submissionLimit: "" });
                        return;
                      }
                      updateSettings({
                        submissionLimit:
                          draft.settings.submissionLimit || "500",
                      });
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value="unlimited">Unlimited submissions</MenuItem>
                    <MenuItem value="limited">Limited submissions</MenuItem>
                  </TextField>
                  {!submissionLimitUnlimited ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max submissions limit"
                      placeholder="e.g. 500"
                      value={draft.settings.submissionLimit ?? ""}
                      onChange={(e) =>
                        updateSettings({ submissionLimit: e.target.value })
                      }
                      inputProps={{ min: 1 }}
                      sx={fieldSx}
                    />
                  ) : null}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: submissionLimitUnlimited ? "1fr 1fr" : "1fr 1fr 1fr",
                  },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Password protection"
                  value="open"
                  onChange={(e) => {
                    const requirePassword = e.target.value === "protected";
                    updateSettings({
                      requirePassword,
                      ...(requirePassword ? {} : { password: "" }),
                    });
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="open">No password</MenuItem>
                  <MenuItem value="protected">Password protected</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Submissions limit"
                  value={
                    submissionLimitUnlimited ? "unlimited" : "limited"
                  }
                  onChange={(e) => {
                    if (e.target.value === "unlimited") {
                      updateSettings({ submissionLimit: "" });
                      return;
                    }
                    updateSettings({
                      submissionLimit:
                        draft.settings.submissionLimit || "500",
                    });
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="unlimited">Unlimited submissions</MenuItem>
                  <MenuItem value="limited">Limited submissions</MenuItem>
                </TextField>
                {!submissionLimitUnlimited ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max submissions limit"
                    placeholder="e.g. 500"
                    value={draft.settings.submissionLimit ?? ""}
                    onChange={(e) =>
                      updateSettings({ submissionLimit: e.target.value })
                    }
                    inputProps={{ min: 1 }}
                    sx={fieldSx}
                  />
                ) : null}
              </Box>
            )}
          </>
        )}
      </Box>

      {!hideGymTv ? (
        <TextField
          select
          fullWidth
          size="small"
          label="GymTV broadcast"
          value={draft.gymTv.enabled ? "enabled" : "disabled"}
          onChange={(e) => {
            const enabled = e.target.value === "enabled";
            const patch = { enabled };
            if (
              enabled &&
              !(draft.gymTv.eligibleStatusIds || []).length &&
              statuses.length
            ) {
              patch.eligibleStatusIds = statuses.map((s) => s.id);
            }
            if (enabled && !draft.gymTv.headline?.trim()) {
              patch.headline = tracker?.name || "";
            }
            updateGymTv(patch);
          }}
          sx={{ ...fieldSx, maxWidth: { sm: 360 } }}
        >
          <MenuItem value="enabled">Broadcast enabled</MenuItem>
          <MenuItem value="disabled">Broadcast disabled</MenuItem>
        </TextField>
      ) : null}

      {/* {draft.gymTv.enabled ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            p: 1.5,
            borderRadius: "10px",
            border: `1px solid ${BORDER}`,
            bgcolor: SURFACE,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 110px" },
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Headline"
              placeholder={tracker?.name || "Tracker headline"}
              value={draft.gymTv.headline || ""}
              onChange={(e) => updateGymTv({ headline: e.target.value })}
              sx={fieldSx}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Show top"
              value={draft.gymTv.showTop ?? 10}
              onChange={(e) =>
                updateGymTv({ showTop: Number(e.target.value) || 0 })
              }
              sx={fieldSx}
              fullWidth
            />
          </Box>
          <FieldLabel>Eligible statuses</FieldLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {statuses.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                Add statuses on the tracker first.
              </Typography>
            ) : (
              statuses.map((status) => {
                const selected = eligibleIds.has(status.id);
                return (
                  <Chip
                    key={status.id}
                    label={status.name || "Untitled"}
                    onClick={() => {
                      const next = new Set(eligibleIds);
                      if (next.has(status.id)) next.delete(status.id);
                      else next.add(status.id);
                      updateGymTv({
                        eligibleStatusIds: Array.from(next),
                      });
                    }}
                    sx={{
                      height: 30,
                      fontWeight: 600,
                      fontSize: 12.5,
                      border: `1px solid ${status.color || BORDER}`,
                      bgcolor: selected
                        ? `${status.color || PRIMARY}22`
                        : "#fff",
                      color: status.color || "#0f172a",
                    }}
                  />
                );
              })
            )}
          </Box>
        </Box>
      ) : null} */}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
          Public page background
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.45, mt: -0.5 }}>
          Shown as the hero image on the public join page. Use a wide landscape
          photo (about 1600px wide).
        </Typography>
        {renderBackgroundPicker(140)}
      </Box>

      {/* <Box>
        <Typography
          sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", mb: 1 }}
        >
          Intake
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={
                  publicLinkAvailable
                    ? draft.settings.acceptingSubmissions !== false
                    : false
                }
                disabled={!publicLinkAvailable}
                onChange={(e) =>
                  updateSettings({ acceptingSubmissions: e.target.checked })
                }
                sx={switchSx}
              />
            }
            label="Accepting submissions"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": { fontSize: 13.5 },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={draft.status === "archived"}
                onChange={(e) => {
                  if (e.target.checked) {
                    push({
                      ...draft,
                      status: "archived",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: false,
                      },
                    });
                  } else {
                    push({
                      ...draft,
                      status: isLive ? "live" : "draft",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: true,
                      },
                    });
                  }
                }}
                sx={switchSx}
              />
            }
            label="Archive this journey"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": { fontSize: 13.5 },
            }}
          />
        </Box>
      </Box> */}

      {!isLive && !isArchived ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            p: 2,
            mb: 1,
            borderRadius: "12px",
            border: `1px solid ${BORDER}`,
            bgcolor: PRIMARY_SOFT,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
              Publish this tracker
            </Typography>
            <Typography
              sx={{ mt: 0.35, fontSize: 12.5, color: MUTED, lineHeight: 1.45 }}
            >
              Status is Draft — public join shows “link not found” until you
              publish. Journey Access must be Public on Basics.
            </Typography>
          </Box>
          <button
            type="button"
            className="small-contained-button"
            disabled={publishing || typeof onPublish !== "function"}
            onClick={() => onPublish?.()}
            style={
              publishing || typeof onPublish !== "function"
                ? { opacity: 0.55, cursor: "not-allowed" }
                : undefined
            }
          >
            {publishing ? "Publishing…" : "Publish"}
          </button>
        </Box>
      ) : null}

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Confirmation message"
        value={draft.settings.confirmationMessage || ""}
        onChange={(e) =>
          updateSettings({ confirmationMessage: e.target.value })
        }
        sx={fieldSx}
      />
      <HelperText>
        Shown on the thank-you screen right after someone submits the public
        join form. Leave it empty to use the default message.
      </HelperText>
    </Box>
  );

  const drawerBody = (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        px: 3,
        py: 0.5,
      }}
    >
      <Section
        icon={<LinkOutlinedIcon fontSize="small" />}
        title="Public link"
        description={
          publicLinkAvailable
            ? "Generated for Public trackers. Share it as a URL or a QR code."
            : "Not available for GymRite members trackers — people join through a programme."
        }
      >
        {!publicLinkAvailable ? (
          <Typography sx={{ fontSize: 13.5, color: MUTED, lineHeight: 1.5 }}>
            This journey is for GymRite members. Switch Journey Access to Public
            on Basics if you need a shared public URL.
          </Typography>
        ) : (
          <Box>
            <Box sx={{ mb: 2.5 }}>
              <PublicLinkEditor
                baseUrl={publicBaseUrl}
                slug={publicSlugValue}
                onSlugChange={setPublicSlug}
                onCopy={copyLink}
                onOpenQr={onOpenQr}
                fallbackSlug={slugFallback}
                size="medium"
              />
              <HelperText>
                Base URL is fixed — clear the slug and type a new one if you
                want a different link.
              </HelperText>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Password"
                  placeholder={
                    draft.settings.requirePassword
                      ? "Enter password"
                      : "Turn on Required to set a password"
                  }
                  value={draft.settings.password || ""}
                  disabled={!draft.settings.requirePassword}
                  onChange={(e) =>
                    updateSettings({ password: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 0.25 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(
                                draft.settings.requirePassword
                              )}
                              onChange={(e) => {
                                const requirePassword = e.target.checked;
                                updateSettings({
                                  requirePassword,
                                  ...(requirePassword ? {} : { password: "" }),
                                });
                              }}
                              sx={switchSx}
                            />
                          }
                          label="Required"
                          labelPlacement="start"
                          sx={{
                            m: 0,
                            ml: 0.5,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: MUTED,
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      bgcolor: draft.settings.requirePassword
                        ? "#fff"
                        : SURFACE,
                      pr: 0.5,
                    },
                  }}
                />
                <HelperText>
                  Useful when the link is only meant for people who bought a
                  programme.
                </HelperText>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Submission limit"
                  placeholder="No limit"
                  value={draft.settings.submissionLimit ?? ""}
                  onChange={(e) =>
                    updateSettings({ submissionLimit: e.target.value })
                  }
                  sx={fieldSx}
                />
                <HelperText>
                  The form closes itself once this many entries come in.
                </HelperText>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  label="Confirmation message"
                  value={draft.settings.confirmationMessage || ""}
                  onChange={(e) =>
                    updateSettings({ confirmationMessage: e.target.value })
                  }
                  sx={fieldSx}
                />
                <HelperText>
                  Shown on the thank-you screen right after someone submits the
                  public join form. Leave it empty to use the default message.
                </HelperText>
              </Box>

              <SettingPanel>
                <SettingRow
                  title="Also send a confirmation email"
                  description="Optional email template. Leave off to show the on-screen message only."
                  control={
                    <Switch
                      checked={Boolean(draft.settings.confirmationEmailEnabled)}
                      onChange={(e) =>
                        updateSettings({
                          confirmationEmailEnabled: e.target.checked,
                        })
                      }
                      sx={switchSx}
                    />
                  }
                />
                {draft.settings.confirmationEmailEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    label="Email template"
                    placeholder="Hi {{first_name}}, thanks for joining {{journey_name}}…"
                    value={draft.settings.confirmationEmailTemplate || ""}
                    onChange={(e) =>
                      updateSettings({
                        confirmationEmailTemplate: e.target.value,
                      })
                    }
                    sx={{ ...fieldSx, mt: 2 }}
                  />
                )}
              </SettingPanel>
            </Box>
          </Box>
        )}
      </Section>

      <Section
        icon={<ImageOutlinedIcon fontSize="small" />}
        title="Public page background"
        description="Hero image on the public join page. Prefer a wide landscape photo (~1600px)."
      >
        {renderBackgroundPicker(160)}
      </Section>

      <Section
        icon={<FileDownloadOutlinedIcon fontSize="small" />}
        title="Excel export"
        description="Every Tracker supports Export to Excel of filtered Tracker Entries."
      >
        <SettingPanel>
          <SettingRow
            title="Enable Excel export"
            description="Export the current filtered entry set when live entries are available."
            control={
              <Switch
                checked={draft.settings.excelExportEnabled !== false}
                onChange={(e) =>
                  updateSettings({ excelExportEnabled: e.target.checked })
                }
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>

      {!hideGymTv && (
      <Section
        icon={<TvOutlinedIcon fontSize="small" />}
        title="GymTV broadcast"
        description="Put ranked entries on the big screen in the gym."
      >
        <SettingPanel sx={{ bgcolor: "#fff" }}>
          <SettingRow
            title="Show this tracker on GymTV"
            control={
              <Switch
                checked={Boolean(draft.gymTv.enabled)}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  const patch = { enabled };
                  if (
                    enabled &&
                    !(draft.gymTv.eligibleStatusIds || []).length &&
                    statuses.length
                  ) {
                    patch.eligibleStatusIds = statuses.map((s) => s.id);
                  }
                  if (enabled && !draft.gymTv.headline?.trim()) {
                    patch.headline = tracker?.name || "";
                  }
                  updateGymTv(patch);
                }}
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>
      )}

      <Section
        icon={<PauseCircleOutlineIcon fontSize="small" />}
        title="Intake"
        description={
          hideGymTv
            ? "Publish makes the public join link live. Disable or archive stops new submissions."
            : "Publish makes the public join link live. Disable or archive stops new submissions."
        }
        last
      >
        {!isLive && !isArchived ? (
          <SettingPanel sx={{ bgcolor: "#fff", mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                  Publish this tracker
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: 12.5, color: MUTED, lineHeight: 1.45 }}>
                  Sets status to Live so the public join URL works. Journey Access
                  must be Public (Basics) for shared links.
                </Typography>
              </Box>
              <button
                type="button"
                className="small-contained-button"
                disabled={publishing || typeof onPublish !== "function"}
                onClick={() => onPublish?.()}
                style={
                  publishing || typeof onPublish !== "function"
                    ? { opacity: 0.55, cursor: "not-allowed" }
                    : undefined
                }
              >
                {publishing ? "Publishing…" : "Publish"}
              </button>
            </Box>
          </SettingPanel>
        ) : null}
        <SettingPanel sx={{ bgcolor: "#fff", mb: 1.5 }}>
          <SettingRow
            title="Accepting submissions"
            description={
              publicLinkAvailable
                ? "Turn off to pause the public link without archiving the journey."
                : "Public intake is off for GymRite members trackers."
            }
            control={
              <Switch
                checked={
                  publicLinkAvailable
                    ? draft.settings.acceptingSubmissions !== false
                    : false
                }
                disabled={!publicLinkAvailable}
                onChange={(e) =>
                  updateSettings({ acceptingSubmissions: e.target.checked })
                }
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
        <SettingPanel sx={{ bgcolor: "#fff" }}>
          <SettingRow
            title="Archive this journey"
            description="Marks the tracker archived and stops intake. You can still open history."
            control={
              <Switch
                checked={draft.status === "archived"}
                onChange={(e) => {
                  if (e.target.checked) {
                    push({
                      ...draft,
                      status: "archived",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: false,
                      },
                    });
                  } else {
                    push({
                      ...draft,
                      status: isLive ? "live" : "draft",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: true,
                      },
                    });
                  }
                }}
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>
    </Box>
  );

  if (isInline) {
    return (
      <Box sx={{ width: "100%" }}>
        {!hideHeader ? header : null}
        {inlineBody}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="tracker-publish-drawer-root"
      PaperProps={{
        className: "tracker-publish-drawer MuiDrawer-paperAnchorRight",
        sx: {
          width: {
            xs: "100% !important",
            sm: "520px !important",
          },
          maxWidth: "100vw !important",
          border: "none",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
        },
      }}
    >
      {header}
      {drawerBody}
    </Drawer>
  );
}
